import { __esDecorate, __runInitializers } from "tslib";
import { StringTag } from '#library/decorator.library.js';
import { isFunction } from '#library/assertion.library.js';
/**
 * Global singleton finalization registry for general cleanup callbacks.
 */
const GLOBAL_FINALIZER_REGISTRY = new FinalizationRegistry((cleanup) => {
    try {
        if (isFunction(cleanup))
            cleanup();
    }
    catch (err) {
        console.error('Finalizer callback error:', err);
    }
});
/**
 * ## Finalizer
 * Wrapper around FinalizationRegistry providing typed, safe resource cleanup
 * callbacks triggered when objects are reclaimed by the Garbage Collector.
 */
let Finalizer = (() => {
    let _classDecorators = [StringTag('Finalizer')];
    let _classDescriptor;
    let _classExtraInitializers = [];
    let _classThis;
    var Finalizer = class {
        static { _classThis = this; }
        static {
            const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create(null) : void 0;
            __esDecorate(null, _classDescriptor = { value: _classThis }, _classDecorators, { kind: "class", name: _classThis.name, metadata: _metadata }, null, _classExtraInitializers);
            Finalizer = _classThis = _classDescriptor.value;
            if (_metadata) Object.defineProperty(_classThis, Symbol.metadata, { enumerable: true, configurable: true, writable: true, value: _metadata });
            __runInitializers(_classThis, _classExtraInitializers);
        }
        #callback;
        constructor(callback) {
            this.#callback = callback;
        }
        /**
         * Returns true to identify this instance as a Finalizer.
         */
        get isFinalizer() {
            return true;
        }
        /**
         * Registers a target object with a held value and optional unregister token.
         *
         * @param target - The object to observe for garbage collection
         * @param heldValue - The value passed to the cleanup callback
         * @param unregisterToken - Optional token to unregister the callback prior to GC
         */
        register(target, heldValue, unregisterToken) {
            const token = unregisterToken ?? Object.create(null);
            const callback = this.#callback;
            GLOBAL_FINALIZER_REGISTRY.register(target, () => {
                callback(heldValue);
            }, token);
        }
        /**
         * Unregisters a previously registered finalizer callback.
         *
         * @param unregisterToken - The token used during registration
         * @returns True if a registration was found and removed
         */
        unregister(unregisterToken) {
            return GLOBAL_FINALIZER_REGISTRY.unregister(unregisterToken);
        }
        /**
         * Registers an object to execute a cleanup callback when garbage collected.
         * Returns a function to cancel the finalizer registration prior to GC.
         *
         * @param target - The target object to observe for garbage collection
         * @param cleanup - The teardown callback to execute upon GC reclamation
         * @param unregisterToken - Optional token used for unregistration (defaults to a unique token object)
         * @returns A teardown function that cancels the finalization registration without retaining target
         */
        static register(target, cleanup, unregisterToken) {
            const token = unregisterToken ?? Object.create(null);
            GLOBAL_FINALIZER_REGISTRY.register(target, cleanup, token);
            return () => GLOBAL_FINALIZER_REGISTRY.unregister(token);
        }
    };
    return Finalizer = _classThis;
})();
export { Finalizer };
